import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  Printer, 
  Layers, 
  RefreshCw, 
  CheckCircle2, 
  ArrowRight, 
  Info, 
  Menu, 
  X,
  FileText,
  Sparkles,
  Zap,
  Gauge,
  ShieldCheck,
  Send,
  ExternalLink,
  ChevronRight,
  MapPin,
  Database
} from "lucide-react";
import { Toaster, toast } from "sonner";
import LandingSelector from "./pages/LandingSelector";
import TshirtManufacturing from "./pages/TshirtManufacturing";
import POD from "./pages/POD";
import AuthCallback from "./pages/AuthCallback";
import PodPortal from "./pages/PodPortal";
import AdminPod from "./pages/AdminPod";
import AdminShipments from "./pages/AdminShipments";
import StartPod from "./pages/StartPod";
import AdminLeads from "./pages/AdminLeads";
import LabelScan from "./pages/LabelScan";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Brand Constants
const BUSINESS_NAME = "Custom tshirt printing co.";
const BUSINESS_SLOGAN = "We print your ideas";
const BUSINESS_EMAIL = "truecolourdtf@gmail.com";
const BUSINESS_MOBILE = "7982491307";
const BUSINESS_ADDRESS = "304, 1st Floor, Chaudhary Market, Maliwara, Nehru Nagar, Ghaziabad, Uttar Pradesh - 201001";
const LOGO_URL = "/images/logo.png";
const BANNER_URL = "/images/dtf-banner.png";
const SIZE_REFERENCE_URL = "/images/size-reference.png";
const PEEL_URL = "/images/dtf-peel.png";

// Fixed roll width for all gang sheets
const SHEET_WIDTH = 24;

// 1 meter ≈ 39.37 inches (for reference display only)
const metersToInches = (m) => Math.round(m * 39.37);

// Per-meter rate based on total length in meters
const getRatePerMeter = (meters) => {
  if (meters <= 1) return 300;
  if (meters <= 5) return 250;
  if (meters <= 10) return 200;
  if (meters <= 20) return 170;
  if (meters <= 50) return 150;
  return 130;
};

// Direct WhatsApp trigger with custom message (INR + meters)
const generateWhatsAppLink = (lengthMeters, qty, price) => {
  const message = `Hi ${BUSINESS_NAME}, I'd like to order a Custom DTF Gang Sheet!
Details:
- Width: ${SHEET_WIDTH}" Roll
- Length: ${lengthMeters} meter(s) (~${metersToInches(lengthMeters)}")
- Quantity: ${qty}
- Estimated Price: ₹${price}

I will share my high-resolution PNG or PDF design here on WhatsApp. Please process my order!`;
  return `https://wa.me/91${BUSINESS_MOBILE}?text=${encodeURIComponent(message)}`;
};

// Pricing Configuration for DTF Gang Sheets (INR per meter, min 1 meter)
const calculateDTFPrice = (lengthMeters, qty) => {
  const meters = Math.max(1, Number(lengthMeters) || 0);
  const rate = getRatePerMeter(meters);
  const total = meters * rate * qty;

  return {
    total: Math.round(total),
    rate,
    meters,
    isMinApplied: Number(lengthMeters) < 1
  };
};

function DTFPage() {
  // Calculator State
  const [length, setLength] = useState(1); // Length in meters (minimum 1)
  const [qty, setQty] = useState(1);
  const [priceDetails, setPriceDetails] = useState({ total: 300, rate: 300, meters: 1, isMinApplied: false });

  // Contact Form State
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsFormDataSubmitting] = useState(false);

  // Recalculate price whenever length or qty changes
  useEffect(() => {
    const details = calculateDTFPrice(Number(length), qty);
    setPriceDetails(details);
  }, [length, qty]);

  // Handle calculator submission (redirects to WhatsApp after saving to backend)
  const handleWhatsAppOrder = async () => {
    try {
      // Save quote configuration to backend first for analytical/order-tracking purposes
      await axios.post(`${API}/quotes`, {
        width: SHEET_WIDTH,
        length: priceDetails.meters,
        quantity: Number(qty),
        price: priceDetails.total
      });
      toast.success("Quote saved! Opening WhatsApp to complete your order.");
    } catch (error) {
      console.error("Error saving quote on backend:", error);
      // Proceed to WhatsApp even if backend save fails so user flow is never blocked
    }
    
    // Redirect to WhatsApp
    const link = generateWhatsAppLink(priceDetails.meters, qty, priceDetails.total);
    window.open(link, "_blank");
  };

  // Submit contact inquiry form
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsFormDataSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We will get back to you shortly.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Inquiry submission failed:", error);
      toast.error("Failed to submit message. Please try calling us directly.");
    } finally {
      setIsFormDataSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-slate-900 selection:text-white">
      {/* Top Banner Contact Bar */}
      <div className="bg-slate-900 text-white text-xs md:text-sm py-2 px-4 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 font-mono">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-[#25D366]" />
              <a href={`tel:${BUSINESS_MOBILE}`} className="hover:underline">+91 {BUSINESS_MOBILE}</a>
            </span>
            <span className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-[#FFD400]" />
              <a href={`mailto:${BUSINESS_EMAIL}`} className="hover:underline">{BUSINESS_EMAIL}</a>
            </span>
          </div>
          <div className="flex items-center gap-2 uppercase tracking-wider text-[10px] bg-slate-800 px-2 py-0.5 rounded">
            <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse"></span>
            Super Fast 24-Hour Dispatch
          </div>
        </div>
      </div>

      {/* Primary Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt={BUSINESS_NAME} 
              className="w-12 h-12 rounded-full border border-slate-900 object-cover"
            />
            <div className="flex flex-col">
              <span className="font-extrabold text-lg sm:text-xl tracking-tight leading-none uppercase">{BUSINESS_NAME}</span>
              <span className="text-[10px] font-mono tracking-widest text-slate-500 uppercase mt-0.5">{BUSINESS_SLOGAN}</span>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center gap-7 font-semibold text-sm tracking-wide uppercase">
            <Link to="/" className="text-slate-600 hover:text-slate-950 transition-colors">Home</Link>
            <Link to="/premiumtshirtmanufacturing" className="text-slate-600 hover:text-slate-950 transition-colors">T-Shirts</Link>
            <a href="#calculator" className="text-slate-600 hover:text-slate-950 transition-colors">Calculator</a>
            <a href="#sizes" className="text-slate-600 hover:text-slate-950 transition-colors">Size Guide</a>
            <Link to="/POD" className="text-slate-600 hover:text-slate-950 transition-colors">POD</Link>
            <a href="#contact" className="text-slate-600 hover:text-slate-950 transition-colors">Contact</a>
            <Link to="/admin" className="text-xs font-mono bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 border border-slate-200 flex items-center gap-1">
              <Database className="w-3 h-3" /> Dashboard
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <a 
              href={`https://wa.me/91${BUSINESS_MOBILE}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all duration-150 border border-slate-950 shadow-[2px_2px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px]"
            >
              <MessageSquare className="w-4 h-4 fill-white text-[#25D366]" />
              Quick WhatsApp Order
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="relative py-16 sm:py-24 border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 text-xs font-mono tracking-wider uppercase text-slate-700">
                <Sparkles className="w-3.5 h-3.5 text-slate-900" /> High-Performance DTF Transfers
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.15] text-slate-900 uppercase">
                Vibrant <span className="bg-[#FFD400] text-slate-950 px-2 box-decoration-clone">True Colors</span>, Ready to Press.
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl font-medium">
                Welcome to <strong className="text-slate-950">{BUSINESS_NAME}</strong>. We deliver commercial-grade, ultra-stretchable DTF gang sheets with spectacular details, unmatched color precision, and ultra-vibrant blacks. No minimums, fast 24h dispatch, and direct order processing via WhatsApp!
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <a 
                  href="#calculator" 
                  data-testid="hero-order-cta"
                  className="bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(255,212,0,0.8)] flex items-center gap-2 hover:translate-y-[-1px]"
                >
                  Estimate Price & Order
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a 
                  href={`https://wa.me/91${BUSINESS_MOBILE}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] flex items-center gap-2 hover:translate-y-[-1px]"
                >
                  <MessageSquare className="w-4 h-4 fill-white" />
                  Chat with Print Expert
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200 max-w-xl">
                <div>
                  <h3 className="font-extrabold text-2xl font-mono text-slate-950">24H</h3>
                  <p className="text-xs uppercase font-bold tracking-wider text-slate-500">Turnaround</p>
                </div>
                <div>
                  <h3 className="font-extrabold text-2xl font-mono text-slate-950">100%</h3>
                  <p className="text-xs uppercase font-bold tracking-wider text-slate-500">Wash-Proof Ink</p>
                </div>
                <div>
                  <h3 className="font-extrabold text-2xl font-mono text-slate-950">5★</h3>
                  <p className="text-xs uppercase font-bold tracking-wider text-slate-500">Industry Quality</p>
                </div>
              </div>
            </div>

            {/* Right Graphics/Image Column */}
            <div className="lg:col-span-5 relative space-y-6">
              <div className="border-2 border-slate-950 p-2 sm:p-3 bg-white shadow-[8px_8px_0px_0px_rgba(15,23,42,1)]">
                <div className="bg-slate-100 overflow-hidden relative border border-slate-200">
                  <img 
                    src={BANNER_URL}
                    alt="DTF Gang Sheets - Premium Quality Custom Transfers"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute top-3 left-3 bg-slate-950 text-white text-[10px] font-mono tracking-widest uppercase px-3 py-1">
                    Direct-to-Film Press
                  </div>
                </div>
              </div>

              {/* Easy to Peel showcase */}
              <div className="border-2 border-slate-950 p-2 sm:p-3 bg-white shadow-[8px_8px_0px_0px_rgba(255,212,0,0.6)]">
                <div className="bg-slate-100 overflow-hidden relative border border-slate-200">
                  <img 
                    src={PEEL_URL}
                    alt="Operator peeling a DTF transfer film off a printed t-shirt"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-center font-extrabold uppercase tracking-widest text-slate-950 text-base sm:text-lg pt-3 pb-1 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#25D366]" />
                  Easy to Peel
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Pricing Calculator Section */}
      <section id="calculator" className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-slate-900">
              Interactive Gang Sheet Calculator
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto font-medium">
              Choose your gang sheet size, enter your parameters, and see exact real-time pricing. Share your finalized specifications directly to our WhatsApp with one click!
            </p>
          </div>

          <div data-testid="gang-sheet-calculator" className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Inputs Column */}
            <div className="md:col-span-7 bg-white border-2 border-slate-950 p-6 sm:p-8 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] space-y-6">
              <h3 className="font-extrabold text-lg uppercase tracking-wide border-b border-slate-200 pb-3 flex items-center gap-2">
                <Printer className="w-5 h-5 text-slate-900" />
                Configure Gang Sheet
              </h3>
              
              {/* Fixed Sheet Width */}
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                  Sheet Width
                </label>
                <div className="flex items-center justify-between py-3.5 px-4 bg-slate-950 text-white border-2 border-slate-950">
                  <span className="font-bold uppercase tracking-wider text-sm">24" Sheet Width</span>
                  <span className="text-xs font-mono bg-slate-800 text-slate-300 px-2 py-1 border border-slate-700 uppercase">Standard</span>
                </div>
                <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">All gang sheets are printed on 24" wide sheets.</p>
              </div>

              {/* Length Parameter (in meters) */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                    Enter Sheet Length (Meters)
                  </label>
                  <span className="text-xs font-mono font-bold bg-slate-100 px-2 py-0.5 border border-slate-200">
                    {priceDetails.meters}{" m (~"}{metersToInches(priceDetails.meters)}{"\")"}
                  </span>
                </div>
                <div className="relative">
                  <input 
                    type="number" 
                    min="1"
                    max="100"
                    step="1"
                    value={length}
                    onChange={(e) => setLength(Math.max(0, Number(e.target.value)))}
                    data-testid="calc-length"
                    className="w-full py-3.5 px-4 bg-white border-2 border-slate-950 font-mono text-lg font-bold focus:outline-none focus:bg-slate-50 text-slate-950 rounded-none"
                    placeholder="e.g. 5 (Minimum 1 meter)"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-sm font-bold text-slate-400">
                    METERS
                  </span>
                </div>
                {/* Hidden input for automated tests targeting fixed width */}
                <input type="number" data-testid="calc-width" value={SHEET_WIDTH} readOnly className="hidden" />
                <div className="pt-2">
                  <input 
                    type="range" 
                    min="1" 
                    max="60" 
                    step="1"
                    value={length} 
                    onChange={(e) => setLength(Number(e.target.value))}
                    className="w-full accent-slate-900"
                  />
                  <div className="flex justify-between text-[10px] font-mono text-slate-400 uppercase tracking-widest">
                    <span>1m</span>
                    <span>10m</span>
                    <span>20m</span>
                    <span>50m</span>
                    <span>60m</span>
                  </div>
                </div>
              </div>

              {/* Live Rate Slab Table */}
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                  Per-Meter Rate Slabs (₹)
                </label>
                <div className="border-2 border-slate-950 divide-y divide-slate-200 text-xs font-mono">
                  {[
                    { label: "1 meter (Sample)", rate: 300, active: priceDetails.meters <= 1 },
                    { label: "2 – 5 meters", rate: 250, active: priceDetails.meters > 1 && priceDetails.meters <= 5 },
                    { label: "6 – 10 meters", rate: 200, active: priceDetails.meters > 5 && priceDetails.meters <= 10 },
                    { label: "11 – 20 meters", rate: 170, active: priceDetails.meters > 10 && priceDetails.meters <= 20 },
                    { label: "21 – 50 meters", rate: 150, active: priceDetails.meters > 20 && priceDetails.meters <= 50 },
                    { label: "51+ meters", rate: 130, active: priceDetails.meters > 50 },
                  ].map((slab) => (
                    <div key={slab.label} className={`flex justify-between items-center px-3 py-2 ${slab.active ? 'bg-slate-950 text-white font-bold' : 'bg-white text-slate-600'}`}>
                      <span>{slab.label}</span>
                      <span>₹{slab.rate}/meter</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quantity Input */}
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                  Quantity (Number of Gang Sheets)
                </label>
                <div className="flex items-center gap-3">
                  <button 
                    type="button"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-12 h-12 flex items-center justify-center border-2 border-slate-950 hover:bg-slate-100 font-extrabold text-lg"
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
                    className="w-20 h-12 text-center border-2 border-slate-950 font-mono text-lg font-bold focus:outline-none"
                  />
                  <button 
                    type="button"
                    onClick={() => setQty(qty + 1)}
                    className="w-12 h-12 flex items-center justify-center border-2 border-slate-950 hover:bg-slate-100 font-extrabold text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Price Display & Actions Column */}
            <div className="md:col-span-5 space-y-6">
              <div className="bg-slate-900 text-white border-2 border-slate-950 p-6 sm:p-8 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] relative overflow-hidden">
                <div className="absolute right-[-20px] top-[-20px] opacity-10">
                  <Printer className="w-48 h-48 rotate-12" />
                </div>
                
                <span className="inline-block text-[10px] font-mono tracking-widest uppercase bg-slate-800 text-slate-300 px-2 py-0.5 mb-4 border border-slate-700">
                  Your Custom Quote
                </span>
                
                <div className="space-y-1">
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">Estimated Total Price</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-extrabold font-mono tracking-tight text-white">
                      {"₹"}<span data-testid="calc-price-display">{priceDetails.total}</span>
                    </span>
                    <span className="text-xs font-mono text-[#FFD400] uppercase tracking-wide">
                      {"("}{qty}{" Sheet"}{qty > 1 ? 's' : ''}{")"}
                    </span>
                  </div>
                </div>

                <div className="border-t border-slate-800 my-6 pt-4 space-y-3 text-sm font-mono">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Width:</span>
                    <span>{SHEET_WIDTH}{"\" Roll"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Length:</span>
                    <span>{priceDetails.meters}{" m (~"}{metersToInches(priceDetails.meters)}{"\")"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Quantity:</span>
                    <span>{qty} copy/copies</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Rate:</span>
                    <span>{"₹"}{priceDetails.rate}{" / meter"}</span>
                  </div>
                  {priceDetails.isMinApplied && (
                    <div className="bg-amber-950/40 border border-amber-900/50 text-amber-300 text-xs p-2 uppercase tracking-wide leading-normal">
                      Note: Minimum order length is 1 meter.
                    </div>
                  )}
                </div>

                <button 
                  type="button"
                  onClick={handleWhatsAppOrder}
                  data-testid="whatsapp-order-btn"
                  className="w-full bg-[#25D366] text-white hover:bg-[#20bd5a] py-4 px-6 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-y-[-1px] transition-all"
                >
                  <MessageSquare className="w-5 h-5 fill-white text-[#25D366]" />
                  Order on WhatsApp
                </button>
              </div>

              {/* Order Policy info box */}
              <div className="bg-slate-100 border border-slate-200 p-5 space-y-3">
                <h4 className="font-extrabold text-xs uppercase tracking-wider flex items-center gap-1 text-slate-800">
                  <Info className="w-4 h-4 text-slate-700" />
                  What Happens Next?
                </h4>
                <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside font-medium leading-relaxed">
                  <li>Click {"'Order on WhatsApp'"} above to share details automatically.</li>
                  <li>Send your high-resolution design file (PNG or PDF, high resolution) on WhatsApp.</li>
                  <li>Our team will quickly inspect the layout and confirm print details.</li>
                  <li>We print and dispatch within 24 hours!</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Size Reference Section */}
      <section id="sizes" className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-slate-900">
              Calculate How Many Stickers In 1 Meter
            </h2>
            <p className="text-slate-600 font-medium">
              See approximately how many designs of each common size fit on a single <strong className="text-slate-950">24" wide</strong> gang sheet. Mix and match any sizes to fill your sheet.
            </p>
          </div>

          <div data-testid="size-reference-grid" className="border-2 border-slate-950 bg-white p-2 sm:p-3 shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]">
            <img
              src={SIZE_REFERENCE_URL}
              alt="DTF Sticker Size Reference on T-Shirt — 4x4 (45 pcs), A4 (6 pcs), A3 (4 pcs), A2 (2 pcs) per 1 meter"
              className="w-full h-auto"
            />
          </div>

          <p className="text-center text-xs font-mono text-slate-400 mt-8 uppercase tracking-wider">
            * Approximate counts on a 24" wide gang sheet (per 1 meter). Final layout optimized by our team.
          </p>
        </div>
      </section>

      {/* Bento Grid Features Section */}
      <section id="features" className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-slate-900">
              Why Choose Our DTF Transfers?
            </h2>
            <p className="text-slate-600 font-medium">
              We focus on premium commercial quality. Our prints are built to endure intensive machine washes while maintaining vivid, eye-catching color output.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="border-2 border-slate-950 p-6 flex flex-col justify-between hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all bg-slate-50">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-slate-950 border border-slate-950 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#FFD400]" />
                </div>
                <h3 className="text-xl font-bold uppercase text-slate-950">Super Fast 24H Turnaround</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Need transfers yesterday? We process and dispatch your custom gang sheets within 24 hours of final artwork confirmation. Fast turnaround without compromising an ounce of print quality.
                </p>
              </div>
              <div className="pt-6 border-t border-slate-200 mt-6 text-xs uppercase font-mono tracking-wider text-slate-400">
                Ready to Press
              </div>
            </div>

            {/* Feature 2 */}
            <div className="border-2 border-slate-950 p-6 flex flex-col justify-between hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all bg-slate-50">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-slate-950 border border-slate-950 flex items-center justify-center">
                  <Gauge className="w-6 h-6 text-[#FFD400]" />
                </div>
                <h3 className="text-xl font-bold uppercase text-slate-950">True Vivid Colors & Deep Blacks</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Using state-of-the-art Japanese color calibration and premium inks, we produce transfers that stand out with brilliant highlight details, exact custom pantone matching, and exceptionally rich blacks.
                </p>
              </div>
              <div className="pt-6 border-t border-slate-200 mt-6 text-xs uppercase font-mono tracking-wider text-slate-500">
                Japanese Color Profiles
              </div>
            </div>

            {/* Feature 3 */}
            <div className="border-2 border-slate-950 p-6 flex flex-col justify-between hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all bg-slate-50">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-slate-950 border border-slate-950 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-[#FFD400]" />
                </div>
                <h3 className="text-xl font-bold uppercase text-slate-950">Premium Stretchable Elastic Ink</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  No cracks, no peeling, and no rigid feeling. Our advanced polyurethane DTF powder makes transfers incredibly thin, lightweight, soft to the touch, and super elastic (perfect for activewear).
                </p>
              </div>
              <div className="pt-6 border-t border-slate-200 mt-6 text-xs uppercase font-mono tracking-wider text-slate-500">
                100+ Commercial Washes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Quality Gallery Showcase */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs uppercase tracking-widest font-mono bg-slate-200 px-3 py-1 text-slate-800">
                Quality Showcase
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold uppercase text-slate-900 leading-tight">
                Brilliant Prints Crafted For Maximum Appeal
              </h2>
              <p className="text-slate-600 leading-relaxed font-medium">
                Our high-density powder adhesion technology secures maximum fine-line detail down to 1-pixel resolution. Whether you are printing intricate logos, full-color photorealistic images, or bold vector text, we guarantee premium fidelity on any fabric.
              </p>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="border border-slate-300 p-4 bg-white">
                  <h4 className="font-bold text-slate-900 uppercase">Cotton & Poly Blends</h4>
                  <p className="text-xs text-slate-500 mt-1">Excellent adhesion and low melting-point powder prevent heat scorches on garments.</p>
                </div>
                <div className="border border-slate-300 p-4 bg-white">
                  <h4 className="font-bold text-slate-900 uppercase">Durable Washing</h4>
                  <p className="text-xs text-slate-500 mt-1">Stands up beautifully to industrial laundering without washing out or cracking.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="border-2 border-slate-950 p-1 bg-white shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]">
                <img 
                  src="https://images.unsplash.com/photo-1562157873-818bc0726f68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGN1c3RvbSUyMHQtc2hpcnRzfGVufDB8fHx8MTc4IzgzNDQwN3ww&ixlib=rb-4.1.0&q=85" 
                  alt="Folded colorful t-shirts" 
                  className="aspect-square object-cover border border-slate-200"
                />
              </div>
              <div className="border-2 border-slate-950 p-1 bg-white shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] mt-8">
                <img 
                  src="https://images.unsplash.com/photo-1600832331197-ad575931911b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHx2aWJyYW50JTIwY29sb3IlMjBwYWxldHRlJTIwc3dhdGNoZXN8ZW58MHx8fHwxNzgyNjM0NDA3fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Color Swatches representing premium inks" 
                  className="aspect-square object-cover border border-slate-200"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DTF File Preparation Guide */}
      <section id="guide" className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-slate-900">
              How to Prepare Your DTF File
            </h2>
            <p className="text-slate-600 font-medium">
              Maximize print accuracy and colors by configuring your designs with these expert standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="border border-slate-300 p-6 space-y-3 bg-slate-50">
              <span className="font-mono text-xs font-extrabold bg-slate-200 px-2 py-1 text-slate-800">STEP 01</span>
              <h4 className="font-bold text-slate-950 uppercase">Set 300 DPI Resolution</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Ensure all logos and layouts are in 300 DPI resolution. Low-res files below 150 DPI can output pixelated or blurry transfers.
              </p>
            </div>
            
            <div className="border border-slate-300 p-6 space-y-3 bg-slate-50">
              <span className="font-mono text-xs font-extrabold bg-slate-200 px-2 py-1 text-slate-800">STEP 02</span>
              <h4 className="font-bold text-slate-950 uppercase">PNG or PDF High Resolution</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                We accept high-resolution PNG (transparent background) or high-resolution PDF files only. Avoid low-resolution screenshots or compressed images.
              </p>
            </div>

            <div className="border border-slate-300 p-6 space-y-3 bg-slate-50">
              <span className="font-mono text-xs font-extrabold bg-slate-200 px-2 py-1 text-slate-800">STEP 03</span>
              <h4 className="font-bold text-slate-950 uppercase">Do Not Mirror Artwork</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Our advanced RIP controller automatically mirrors your artwork prior to printing. Send us files in normal forward reading orientation.
              </p>
            </div>

            <div className="border border-slate-300 p-6 space-y-3 bg-slate-50">
              <span className="font-mono text-xs font-extrabold bg-slate-200 px-2 py-1 text-slate-800">STEP 04</span>
              <h4 className="font-bold text-slate-950 uppercase">CMYK Color Space</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Design in CMYK color space. While we support RGB conversions, native CMYK files guarantee the most accurate translation of on-screen colors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="contact" className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs uppercase tracking-widest font-mono bg-slate-200 px-3 py-1 text-slate-800">
                Contact Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold uppercase text-slate-900 leading-none">
                Get In Touch For Large Volume Quotes
              </h2>
              <p className="text-slate-600 leading-relaxed font-medium">
                Have bulk requirements, continuous orders, or custom sizing needs? Drop us a line. Our commercial consultants will configure a dedicated discount plan tailored to your operation.
              </p>

              <div className="space-y-4 pt-4 border-t border-slate-200 font-mono text-sm text-slate-700">
                <p className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-900" />
                  <strong>Phone:</strong> <a href={`tel:${BUSINESS_MOBILE}`} className="hover:underline">+91 {BUSINESS_MOBILE}</a>
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-900" />
                  <strong>Email:</strong> <a href={`mailto:${BUSINESS_EMAIL}`} className="hover:underline">{BUSINESS_EMAIL}</a>
                </p>
                <p className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-900 shrink-0 mt-0.5" />
                  <span><strong>Address:</strong> {BUSINESS_ADDRESS}</span>
                </p>
              </div>
            </div>

            {/* Right Form Card Column */}
            <div className="lg:col-span-7">
              <div className="bg-white border-2 border-slate-950 p-6 sm:p-8 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]">
                <h3 className="font-extrabold text-lg uppercase tracking-wide border-b border-slate-200 pb-3 mb-6 flex items-center gap-2">
                  <Send className="w-5 h-5 text-slate-900" />
                  Send Instant Message
                </h3>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="contact-name"
                        className="w-full p-3 bg-white border border-slate-300 focus:border-slate-950 focus:outline-none rounded-none text-slate-950"
                        placeholder="Your Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <input 
                        type="email" 
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="contact-email"
                        className="w-full p-3 bg-white border border-slate-300 focus:border-slate-950 focus:outline-none rounded-none text-slate-950"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                      Phone Number (Optional)
                    </label>
                    <input 
                      type="text" 
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-3 bg-white border border-slate-300 focus:border-slate-950 focus:outline-none rounded-none text-slate-950"
                      placeholder="e.g. +91 99999 99999"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                      Message / Design description <span className="text-rose-500">*</span>
                    </label>
                    <textarea 
                      required
                      rows="4"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      data-testid="contact-message"
                      className="w-full p-3 bg-white border border-slate-300 focus:border-slate-950 focus:outline-none rounded-none text-slate-950"
                      placeholder="Describe your printing requirements..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="w-full bg-slate-950 text-white hover:bg-slate-800 py-3.5 px-6 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px] transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? "Sending..." : "Submit Inquiry"}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 border-b border-slate-900 pb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full border border-slate-800" />
                <span className="font-extrabold text-md uppercase tracking-tight">{BUSINESS_NAME}</span>
              </div>
              <p className="text-xs text-slate-400 font-medium">
                Premium quality Direct-to-Film Transfers built to scale your clothing brand. Wash-proof, stretchable, and incredibly vibrant.
              </p>
            </div>
            
            <div>
              <h4 className="font-extrabold text-xs uppercase tracking-widest text-slate-300 mb-4">Quick Links</h4>
              <ul className="space-y-2 text-xs text-slate-400 font-mono uppercase">
                <li><a href="#hero" className="hover:underline hover:text-white">Home</a></li>
                <li><a href="#calculator" className="hover:underline hover:text-white">Pricing Calculator</a></li>
                <li><a href="#features" className="hover:underline hover:text-white">Key Features</a></li>
                <li><a href="#guide" className="hover:underline hover:text-white">Prep Guide</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-extrabold text-xs uppercase tracking-widest text-slate-300 mb-4">Contact Info</h4>
              <ul className="space-y-2 text-xs text-slate-400 font-mono">
                <li>Email: {BUSINESS_EMAIL}</li>
                <li>Phone: +91 {BUSINESS_MOBILE}</li>
                <li className="leading-relaxed">Address: {BUSINESS_ADDRESS}</li>
              </ul>
            </div>

            <div>
              <h4 className="font-extrabold text-xs uppercase tracking-widest text-slate-300 mb-4">Print Policies</h4>
              <p className="text-xs text-slate-400 leading-relaxed font-medium">
                Artwork is printed exactly as supplied. Ensure transparent background and 300 DPI for premium output. Dispatch within 24 hours of approval.
              </p>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row justify-between items-center text-xs text-slate-500 font-mono">
            <p>© {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.</p>
            <p className="flex items-center gap-1.5 mt-2 sm:mt-0">
              Made with precision for custom apparel makers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function AdminDashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);

  const fetchDashboardData = async () => {
    try {
      const [contactsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/quotes`)
      ]);
      setInquiries(contactsRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      toast.error("Failed to load data from backend server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchDashboardData();
    }
  }, [isAuthorized]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === "truecolourdtf") {
      setIsAuthorized(true);
      toast.success("Authorized successfully!");
    } else {
      toast.error("Incorrect dashboard password.");
    }
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border-2 border-slate-950 p-6 sm:p-8 shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] space-y-6">
          <div className="text-center">
            <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-full border border-slate-900 mx-auto object-cover" />
            <h1 className="text-2xl font-black uppercase mt-3 tracking-tight">{BUSINESS_NAME}</h1>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-1">Admin Dashboard Login</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">
                Dashboard Password
              </label>
              <input 
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 bg-white border border-slate-300 focus:border-slate-950 focus:outline-none rounded-none text-slate-950 font-mono text-center text-lg"
                placeholder="••••••••"
              />
              <p className="text-[10px] text-slate-400 font-mono text-center">Hint: truecolourdtf</p>
            </div>
            
            <button 
              type="submit"
              className="w-full bg-slate-950 text-white hover:bg-slate-850 py-3.5 font-bold uppercase tracking-wider border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px] transition-all"
            >
              Access Dashboard
            </button>
          </form>
          <div className="text-center pt-2">
            <Link to="/" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center justify-center gap-1">
              {"← Return to Landing Page"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border-2 border-slate-950 p-6 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-12 h-12 rounded-full border border-slate-900 object-cover" />
            <div>
              <h1 className="text-2xl font-black uppercase tracking-tight">{BUSINESS_NAME}</h1>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-400 font-mono">Owner Management Terminal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => fetchDashboardData()}
              className="px-4 py-2 text-xs font-mono font-bold bg-slate-100 hover:bg-slate-200 border border-slate-300 flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh Data
            </button>
            <Link to="/" className="px-4 py-2 text-xs font-semibold bg-slate-950 text-white hover:bg-slate-800 border border-slate-950 flex items-center gap-1">
              Home Landing
            </Link>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-6 flex flex-col justify-between">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Total Leads Inquiries</p>
            <p className="text-4xl font-extrabold font-mono mt-2 text-slate-900">{inquiries.length}</p>
          </div>
          <div className="bg-white border border-slate-200 p-6 flex flex-col justify-between">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Estimated Custom Quotes</p>
            <p className="text-4xl font-extrabold font-mono mt-2 text-slate-900">{quotes.length}</p>
          </div>
          <div className="bg-white border border-slate-200 p-6 flex flex-col justify-between bg-slate-950 text-white">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-300">Target Contact Email</p>
            <p className="text-md font-extrabold font-mono mt-2 truncate">{BUSINESS_EMAIL}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contacts/Inquiries Panel */}
          <div className="bg-white border-2 border-slate-950 p-6 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] space-y-4">
            <h3 className="font-extrabold text-md uppercase tracking-wide border-b border-slate-200 pb-2 flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-800" />
              Customer Inquiry Messages
            </h3>
            
            {loading ? (
              <p className="text-xs text-slate-400 font-mono py-8 text-center">Loading inquiries...</p>
            ) : inquiries.length === 0 ? (
              <p className="text-xs text-slate-400 font-mono py-8 text-center">No messages received yet.</p>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {inquiries.map((item, index) => (
                  <div key={item.id || index} className="p-4 bg-slate-50 border border-slate-200 space-y-2 text-xs">
                    <div className="flex justify-between items-start font-mono text-[10px] text-slate-400">
                      <span>{new Date(item.timestamp).toLocaleString()}</span>
                      <span className="font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5">Leads</span>
                    </div>
                    <p className="text-sm font-bold text-slate-950">{item.name}</p>
                    <p className="text-slate-500">
                      <strong>Email:</strong> {item.email} {item.phone && ` | Phone: ${item.phone}`}
                    </p>
                    <p className="text-slate-700 bg-white border border-slate-150 p-2 italic leading-relaxed whitespace-pre-wrap">
                      {"\"" + item.message + "\""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quotes Panel */}
          <div className="bg-white border-2 border-slate-950 p-6 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] space-y-4">
            <h3 className="font-extrabold text-md uppercase tracking-wide border-b border-slate-200 pb-2 flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-800" />
              Recent Gang Sheet Calculations
            </h3>

            {loading ? (
              <p className="text-xs text-slate-400 font-mono py-8 text-center">Loading calculation history...</p>
            ) : quotes.length === 0 ? (
              <p className="text-xs text-slate-400 font-mono py-8 text-center">No custom sheets estimated yet.</p>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {quotes.map((item, index) => (
                  <div key={item.id || index} className="p-4 bg-slate-50 border border-slate-200 space-y-2 text-xs font-mono">
                    <div className="flex justify-between items-center text-[10px] text-slate-400">
                      <span>{new Date(item.timestamp).toLocaleString()}</span>
                      <span className="font-bold text-slate-950 bg-[#FFD400] px-1.5 py-0.5">Quote</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Width</p>
                        <p className="font-bold text-slate-800">{item.width + '" roll'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Length</p>
                        <p className="font-bold text-slate-800">{item.length + ' meter(s)'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Quantity</p>
                        <p className="font-bold text-slate-800">{item.quantity} copy/copies</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Est. Price</p>
                        <p className="font-extrabold text-slate-950">₹{item.price}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Handle Emergent Google Auth callback fragment before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingSelector />} />
      <Route path="/dtf" element={<DTFPage />} />
      <Route path="/premiumtshirtmanufacturing" element={<TshirtManufacturing />} />
      <Route path="/POD" element={<POD />} />
      <Route path="/startpod" element={<StartPod />} />
      <Route path="/admin/leads" element={<AdminLeads />} />
      <Route path="/label-scan" element={<LabelScan />} />
      <Route path="/pod-portal" element={<PodPortal />} />
      <Route path="/admin-pod" element={<AdminPod />} />
      <Route path="/admin-shipments" element={<AdminShipments />} />
      <Route path="/admin" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
